import { Clock, DollarSign, Lock, Calendar, CheckCircle } from 'lucide-react';

interface AppointmentType {
  id: string;
  name: string;
  category: string;
  duration_minutes: number;
  price_cents: number;
  description: string;
  sort_order: number;
}

interface EligibilityStatus {
  appointmentTypeId: string;
  isEligible: boolean;
  reason?: string;
  nextAvailableDate?: string;
  hasCompleted?: boolean;
}

interface AppointmentTypeCardProps {
  appointmentType: AppointmentType;
  eligibility: EligibilityStatus;
  onBook: () => void;
}

export function AppointmentTypeCard({ 
  appointmentType, 
  eligibility, 
  onBook 
}: AppointmentTypeCardProps) {
  const isLocked = !eligibility.isEligible;
  const isCompleted = eligibility.hasCompleted;
  
  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  const getCardIcon = () => {
    switch (appointmentType.id) {
      case 'initial-consultation':
        return '🌱';
      case 'follow-up-60':
        return '🔄';
      case 'follow-up-30':
        return '⚡';
      default:
        return '📅';
    }
  };
  
  return (
    <div className={`
      bg-white rounded-lg shadow-md border transition-all duration-200
      ${isLocked ? 'opacity-60 border-gray-200' : 'border-gray-200 hover:border-green-300 hover:shadow-lg'}
      ${isCompleted ? 'border-green-200 bg-green-50' : ''}
    `}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getCardIcon()}</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                {appointmentType.name}
                {isCompleted && (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {appointmentType.category === 'individual' ? 'One-on-One Session' : 'Group Session'}
              </p>
            </div>
          </div>
          {isLocked && <Lock className="w-5 h-5 text-gray-400 flex-shrink-0" />}
        </div>
        
        {/* Description */}
        <p className="text-gray-700 mb-4 text-sm leading-relaxed">
          {appointmentType.description}
        </p>
        
        {/* Details */}
        <div className="flex items-center gap-6 mb-4 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4" />
            <span>{appointmentType.duration_minutes} min</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <DollarSign className="w-4 h-4" />
            <span className="font-medium">{formatPrice(appointmentType.price_cents)}</span>
          </div>
        </div>
        
        {/* Status & Action */}
        {isCompleted ? (
          <div className="bg-green-100 rounded-lg p-3 border border-green-200">
            <div className="flex items-center gap-2 text-green-800 mb-2">
              <CheckCircle className="w-4 h-4" />
              <span className="font-medium">Completed</span>
            </div>
            <p className="text-green-700 text-sm">
              You can book another session of this type
            </p>
            <button
              onClick={onBook}
              className="mt-3 w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors font-medium"
            >
              Book Another Session
            </button>
          </div>
        ) : isLocked ? (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-start gap-2 text-gray-600">
              <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-700 mb-1">Not Available Yet</p>
                <p className="text-sm">{eligibility.reason}</p>
                {eligibility.nextAvailableDate && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>
                      Available {new Date(eligibility.nextAvailableDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={onBook}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Calendar className="w-4 h-4" />
            Book This Session
          </button>
        )}
      </div>
    </div>
  );
}